import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormLoading(false);
      return;
    }

    try {
      const formUrl = 'https://readdy.ai/api/form/d9sqspd8o1vn02ekukl0';
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = (parsed && (parsed as Record<string, unknown>).message) || '提交失败，请稍后重试';
        setFormError(String(msg));
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />
        </div>

        {/* Brand Introduction */}
        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-3">
                关于正品蓝导航
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                做最值得信赖的网址导航平台，让每一次浏览都安全放心
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center mb-14">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  正品蓝导航由一群热爱互联网、关注网络安全的资深从业者于2023年创立。创始团队拥有超过十年的互联网行业经验，深刻理解用户在寻找可靠网站时的痛点。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  在搜索引擎结果充斥着广告和推广链接、假冒仿冒网站层出不穷的今天，我们立志打造一个让用户完全信赖的导航平台——每一个链接都经过严格筛选，每一个推荐都基于真实品质。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  自上线以来，正品蓝导航已收录超过5,000个正品网站，累计服务超过500万用户。我们持续优化产品体验，做用户最信赖的导航平台。
                </p>
              </div>
              <div className="rounded-xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20professional%20workspace%20with%20a%20compass%20decorative%20element%2C%20clean%20minimal%20office%20setting%20with%20soft%20beige%20and%20cream%20tones%2C%20trustworthy%20atmosphere%2C%20gentle%20natural%20lighting%20through%20windows%2C%20editorial%20photography%20style%2C%20warm%20wooden%20desk%20with%20a%20laptop&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="正品蓝导航品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Core Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                我们的核心价值
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                正品、安全、纯净 —— 不变的承诺
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '正品承诺',
                  desc: '每个收录网站均经过人工严格审核，确保域名真实、资质合法。我们坚决杜绝假冒仿冒网站，为用户提供100%正品链接。',
                },
                {
                  icon: 'ri-lock-line',
                  title: '安全守护',
                  desc: '持续监控收录网站的安全状态，定期进行安全扫描检测。用户可通过反馈渠道举报可疑链接，我们承诺24小时内响应处理。',
                },
                {
                  icon: 'ri-eye-off-line',
                  title: '纯净体验',
                  desc: '坚持无广告、无弹窗、无恶意跳转的纯净浏览体验。不为商业利益妥协用户体验，让导航回归纯粹。',
                },
                {
                  icon: 'ri-heart-line',
                  title: '用户至上',
                  desc: '倾听每一位用户的声音，持续优化产品体验。用户的信任是我们最宝贵的财富，也是我们不断前进的动力。',
                },
              ].map((val, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-accent-100">
                    <i className={`${val.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {val.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {val.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                发展历程
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                一步一个脚印，用心做好导航
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300"></div>
              <div className="flex flex-col gap-8">
                {[
                  { year: '2023年6月', title: '项目启动', desc: '正品蓝导航项目正式启动，创始团队组建完成' },
                  { year: '2023年9月', title: '首批收录', desc: '完成首批500个权威网站的人工审核与收录' },
                  { year: '2024年1月', title: '正式上线', desc: '正品蓝导航网站正式上线运营，面向全网用户开放' },
                  { year: '2024年3月', title: '用户突破', desc: '日活跃用户突破10万，获得广泛好评' },
                  { year: '2024年6月', title: '规模扩展', desc: '收录网站超过2,000个，覆盖20个分类领域' },
                  { year: '至今', title: '持续优化', desc: '持续扩充收录资源，优化产品体验，服务更多用户' },
                ].map((milestone, idx) => (
                  <div
                    key={idx}
                    className={`relative flex items-start gap-6 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-[11px] md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block text-xs font-semibold text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1">
                        {milestone.title}
                      </h3>
                      <p className="text-sm text-foreground-600">
                        {milestone.desc}
                      </p>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                有任何问题或建议？欢迎随时与我们联系
              </p>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复您</p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="mt-6 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} data-readdy-form="">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact_subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact_message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {/* Honeypot */}
                  <div className="honeypot-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  {formError && (
                    <p className="text-sm text-red-500 mb-4">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={formLoading}
                    className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formLoading ? '提交中...' : '提交留言'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@zhengpinlan.com' },
                { icon: 'ri-phone-line', title: '客服电话', value: '400-XXX-XXXX' },
                { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市海淀区中关村科技园区' },
              ].map((info, idx) => (
                <div key={idx} className="text-center p-5 border border-background-200/70 rounded-xl">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className={`${info.icon} text-lg text-primary-600`}></i>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">{info.title}</h4>
                  <p className="text-sm text-foreground-600">{info.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "正品蓝导航",
            "url": "https://www.zhengpinlan.com",
            "description": "专业权威的网址导航平台，汇聚海量正品网站资源，精准分类、安全可信",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "contact@zhengpinlan.com",
              "telephone": "400-XXX-XXXX"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "北京",
              "addressRegion": "海淀区",
              "streetAddress": "中关村科技园区"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.zhengpinlan.com" },
              { "@type": "ListItem", "position": 2, "name": "关于我们" },
            ]
          })
        }}
      />
    </>
  );
}